import { Button } from '@/components/ui/button';
import { Package, Star } from 'lucide-react';
import { playGameSound } from '@/utils/sound-utils';

interface DeckEmptyStateProps {
  readonly type: 'all' | 'default' | 'free';
  readonly searchQuery?: string;
  readonly onReset?: () => void;
  readonly onAction?: () => void;
}

export function DeckEmptyState({ 
  type, 
  searchQuery, 
  onReset, 
  onAction 
}: Readonly<DeckEmptyStateProps>) {
  const getContent = () => {
    switch (type) {
      case 'default':
        return {
          icon: <Star className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />,
          title: 'No Default Deck Found',
          description: 'You haven\'t set a default deck yet. Choose a deck and set it as default.',
          actionText: 'View All Decks',
        };
      case 'free':
        return {
          icon: <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />,
          title: 'No Free Decks Found',
          description: 'No free decks match your current filters. Try adjusting your search or filter settings.',
          actionText: 'Reset Filters',
        };
      default:
        return {
          icon: <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />,
          title: 'No Decks Found',
          description: searchQuery 
            ? 'No decks match your search query. Try adjusting your search.'
            : 'You haven\'t acquired any card decks yet. Visit the marketplace to browse available decks.',
          actionText: searchQuery ? 'Reset Filters' : 'Browse Deck Marketplace',
        };
    }
  };

  const content = getContent();

  const handleAction = () => {
    playGameSound('ui.button');
    if (searchQuery) {
      onReset?.();
    } else {
      onAction?.();
    }
  };

  return (
    <div className="bg-black/60 backdrop-blur-sm rounded-xl border border-primary/30 p-8 text-center mt-4">
      {content.icon}
      <h3 className="text-lg font-medium mb-2">{content.title}</h3>
      <p className="text-muted-foreground mb-4">
        {content.description}
      </p>
      <Button
        onClick={handleAction}
        className={searchQuery ? "bg-red-600 hover:bg-red-700 text-white shadow-md border-0 font-medium" : "bg-primary hover:bg-primary/90 text-white"}
      >
        {content.actionText}
      </Button>
    </div>
  );
}